import React, { useState, useEffect, useCallback, memo, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Moon,
  Sun,
  Bell,
  Shield,
  User,
  Target,
  DollarSign,
  Download,
  Users,
  TrendingUp,
  Settings,
  Mail,
  Smartphone,
  Key,
  Clock,
  Save,
  Check,
  Command,
  Search,
  Edit3,
  Calculator,
  Calendar,
  BarChart3,
  Zap,
  Sparkles,
  Brain,
  AlertCircle,
  ArrowUpRight,
  ArrowDownRight,
  RefreshCw,
  Trash2,
  LogOut,
  Eye,
  Percent,
  Activity,
  PieChart
} from 'lucide-react';
import { useTheme } from '../hooks/useTheme';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { format, addMonths, differenceInDays } from 'date-fns';
import { Tooltip } from '../components/ui/tooltip';
import { GOALS_CONFIG } from '../lib/goalCalculations';
import GoalsSettings from '../components/settings/GoalsSettings';

// Constants
const RESUME_PRICE = 4.99; // Standard plan price
const SUBSCRIPTION_PRICE = 9.99;
const TARGET_REVENUE_MONTH_6 = 5000;

// Memoized components with simplified animations
const MemoizedSettingRow = memo(({ icon: Icon, label, description, children, className = "" }) => (
  <div className={`flex items-center justify-between py-3 ${className}`}>
    <div className="flex items-start gap-3 flex-1">
      <div className="mt-0.5">
        <Icon className="h-4 w-4 text-neutral-500 dark:text-neutral-400" />
      </div>
      <div className="flex-1">
        <div className="font-medium text-sm text-neutral-900 dark:text-neutral-50">
          {label}
        </div>
        {description && (
          <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5 leading-relaxed">
            {description}
          </div>
        )}
      </div>
    </div>
    <div className="flex-shrink-0">
      {children}
    </div>
  </div>
));

const LinearSettings = () => {
  const { theme, isDark, toggleTheme, setThemeMode } = useTheme();
  const { logout } = useAuth();
  const [isSaved, setIsSaved] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Revenue goal state with enhanced tracking
  const [revenueGoal, setRevenueGoal] = useState(() => {
    const saved = localStorage.getItem('revenueGoal');
    const defaultGoal = {
      amount: TARGET_REVENUE_MONTH_6,
      startDate: new Date().toISOString().split('T')[0],
      duration: 6,
      currentProgress: 0,
      historicalData: {
        last30Days: 1200,
        last90Days: 3500,
        trend: 'up'
      }
    };

    if (saved) {
      const parsed = JSON.parse(saved);
      // Ensure historicalData exists with default values if missing
      return {
        ...parsed,
        historicalData: parsed.historicalData || defaultGoal.historicalData
      };
    }

    return defaultGoal;
  });

  // Goals Configuration state
  const [goalsConfig, setGoalsConfig] = useState(() => {
    const saved = localStorage.getItem('goalsConfig');
    return saved ? JSON.parse(saved) : GOALS_CONFIG;
  });

  // AI preferences state
  const [aiPreferences, setAiPreferences] = useState(() => {
    const saved = localStorage.getItem('aiPreferences');
    return saved ? JSON.parse(saved) : {
      personalizedSuggestions: true,
      autoGenerateSuggestions: false,
      aiInsights: true
    };
  });

  // App preferences state
  const [appPreferences, setAppPreferences] = useState(() => {
    const saved = localStorage.getItem('appPreferences');
    return saved ? JSON.parse(saved) : {
      emailNotifications: true,
      pushNotifications: false,
      featurePreviews: true
    };
  });

  // Calculate AI predictions and insights
  const revenueInsights = useMemo(() => {
    const monthlyTarget = revenueGoal.amount / revenueGoal.duration;
    const daysElapsed = differenceInDays(new Date(), new Date(revenueGoal.startDate));
    const daysTotal = revenueGoal.duration * 30;
    const expectedProgress = (daysElapsed / daysTotal) * revenueGoal.amount;
    const isOnTrack = revenueGoal.currentProgress >= expectedProgress;

    // Calculate trend-based prediction with fallback
    const historicalData = revenueGoal.historicalData || {
      last30Days: 0,
      last90Days: 0,
      trend: 'up'
    };

    const trendFactor = historicalData.trend === 'up' ? 1.1 : 0.9;
    const predictedCompletion = revenueGoal.currentProgress +
      (historicalData.last30Days * trendFactor * (revenueGoal.duration - 1));

    // Calculate monthly metrics
    const monthlyProgress = revenueGoal.currentProgress / (daysElapsed / 30);
    const monthlyIncrease = ((monthlyProgress - historicalData.last30Days) / historicalData.last30Days) * 100;
    const requiredMonthlyIncrease = ((monthlyTarget - monthlyProgress) / monthlyProgress) * 100;

    return {
      monthlyTarget,
      expectedProgress,
      isOnTrack,
      predictedCompletion,
      completionConfidence: Math.min((predictedCompletion / revenueGoal.amount) * 100, 100),
      monthlyProgress,
      monthlyIncrease,
      requiredMonthlyIncrease
    };
  }, [revenueGoal]);

  // Save handlers with enhanced feedback
  const handleSave = useCallback(() => {
    setIsLoading(true);
    setIsSaved(true);

    // Save all preferences
    localStorage.setItem('revenueGoal', JSON.stringify(revenueGoal));
    localStorage.setItem('goalsConfig', JSON.stringify(goalsConfig));
    localStorage.setItem('aiPreferences', JSON.stringify(aiPreferences));
    localStorage.setItem('appPreferences', JSON.stringify(appPreferences));

    setTimeout(() => {
      setIsLoading(false);
      setTimeout(() => setIsSaved(false), 2000);
    }, 1000);
  }, [revenueGoal, goalsConfig, aiPreferences, appPreferences]);

  // Reset handlers
  const handleResetGoals = useCallback(() => {
    if (window.confirm('Are you sure you want to reset all revenue goals? This cannot be undone.')) {
      setRevenueGoal({
        amount: TARGET_REVENUE_MONTH_6,
        startDate: new Date().toISOString().split('T')[0],
        duration: 6,
        currentProgress: 0,
        historicalData: {
          last30Days: 0,
          last90Days: 0,
          trend: 'up'
        }
      });
    }
  }, []);

  const handleResetGoalsConfig = useCallback(() => {
    if (window.confirm('Are you sure you want to reset all tracking goals to defaults? This cannot be undone.')) {
      setGoalsConfig(GOALS_CONFIG);
    }
  }, []);

  // Update handlers
  const updateRevenueGoal = useCallback((field, value) => {
    setRevenueGoal(prev => ({ ...prev, [field]: value }));
  }, []);

  const updateGoalsConfig = useCallback((category, subcategory, field, value) => {
    setGoalsConfig(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [subcategory]: {
          ...prev[category]?.[subcategory],
          [field]: value
        }
      }
    }));
  }, []);

  const updateAiPreferences = useCallback((field, value) => {
    setAiPreferences(prev => ({ ...prev, [field]: value }));
  }, []);

  const updateAppPreferences = useCallback((field, value) => {
    setAppPreferences(prev => ({ ...prev, [field]: value }));
  }, []);

  // Calculate progress percentage
  const progressPercentage = useMemo(() => {
    return Math.min((revenueGoal.currentProgress / revenueGoal.amount) * 100, 100);
  }, [revenueGoal.currentProgress, revenueGoal.amount]);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="sticky top-0 z-50 backdrop-blur-xl border-b border-border bg-background/80">
        <div className="flex items-center justify-between gap-8 px-8 py-6">
          <div className="flex items-center gap-6 flex-1 max-w-2xl">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <Settings className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h1 className="text-xl font-semibold tracking-tight text-foreground">Settings</h1>
                <p className="text-xs text-muted-foreground">
                  Manage preferences and revenue goals
                </p>
              </div>
            </div>

            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search settings..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-11 pr-16 py-3 rounded-xl text-sm bg-muted border border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/30 transition-all duration-200"
              />
              <div className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-border bg-muted text-muted-foreground">
                <Command className="h-3 w-3" />
                <span className="font-medium">K</span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="flex items-center space-x-2">
              <Sun className={`h-4 w-4 ${!isDark ? 'text-amber-500' : 'text-muted-foreground'}`} />
              <Switch
                checked={isDark}
                onCheckedChange={toggleTheme}
                className="data-[state=checked]:bg-primary"
              />
              <Moon className={`h-4 w-4 ${isDark ? 'text-blue-500' : 'text-muted-foreground'}`} />
            </div>

            <div className="h-6 w-px bg-border" />

            <Button
              onClick={handleSave}
              disabled={isLoading}
              className={`flex items-center gap-2 ${isSaved ? 'bg-muted text-primary' : 'bg-primary text-primary-foreground'
                }`}
            >
              {isSaved ? <Check className="h-4 w-4" /> : <Save className="h-4 w-4" />}
              {isSaved ? 'Saved' : 'Save changes'}
            </Button>

            <Button
              variant="ghost"
              onClick={logout}
              className="flex items-center gap-2 text-destructive hover:text-destructive/90 hover:bg-destructive/10"
            >
              <LogOut className="h-4 w-4" />
              Logout
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-8 py-8">
        <Tabs defaultValue="preferences" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="preferences">Preferences</TabsTrigger>
            <TabsTrigger value="goals">Goals & Tracking</TabsTrigger>
            <TabsTrigger value="revenue">Revenue Goals</TabsTrigger>
            <TabsTrigger value="advanced">Advanced</TabsTrigger>
          </TabsList>

          <TabsContent value="preferences" className="space-y-6">
            <div className="grid gap-6 lg:grid-cols-2">
              {/* Theme & Preferences */}
              <Card className="bg-card text-card-foreground shadow-sm hover:shadow-md transition-all duration-200">
                <CardHeader>
                  <CardTitle className="text-foreground">Theme & Preferences</CardTitle>
                  <CardDescription className="text-muted-foreground">
                    Customize your app experience
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-primary/10">
                          <Sparkles className="h-4 w-4 text-primary" />
                        </div>
                        <div>
                          <Label className="text-sm font-medium">Feature Previews</Label>
                          <p className="text-xs text-muted-foreground">
                            Get early access to new features
                          </p>
                        </div>
                      </div>
                      <Switch
                        checked={appPreferences.featurePreviews}
                        onCheckedChange={(checked) => updateAppPreferences('featurePreviews', checked)}
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-primary/10">
                          <Bell className="h-4 w-4 text-primary" />
                        </div>
                        <div>
                          <Label className="text-sm font-medium">Email Notifications</Label>
                          <p className="text-xs text-muted-foreground">
                            Receive updates via email
                          </p>
                        </div>
                      </div>
                      <Switch
                        checked={appPreferences.emailNotifications}
                        onCheckedChange={(checked) => updateAppPreferences('emailNotifications', checked)}
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-primary/10">
                          <Smartphone className="h-4 w-4 text-primary" />
                        </div>
                        <div>
                          <Label className="text-sm font-medium">Push Notifications</Label>
                          <p className="text-xs text-muted-foreground">
                            Get instant updates on your device
                          </p>
                        </div>
                      </div>
                      <Switch
                        checked={appPreferences.pushNotifications}
                        onCheckedChange={(checked) => updateAppPreferences('pushNotifications', checked)}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* AI Usage Preferences */}
              <Card className="bg-card text-card-foreground shadow-sm hover:shadow-md transition-all duration-200">
                <CardHeader>
                  <CardTitle className="text-foreground">AI Usage Preferences</CardTitle>
                  <CardDescription className="text-muted-foreground">
                    Control how AI enhances your experience
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-primary/10">
                          <Brain className="h-4 w-4 text-primary" />
                        </div>
                        <div>
                          <Label className="text-sm font-medium">Personalized Suggestions</Label>
                          <p className="text-xs text-muted-foreground">
                            Get AI-powered recommendations based on your usage
                          </p>
                        </div>
                      </div>
                      <Switch
                        checked={aiPreferences.personalizedSuggestions}
                        onCheckedChange={(checked) => updateAiPreferences('personalizedSuggestions', checked)}
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-primary/10">
                          <Zap className="h-4 w-4 text-primary" />
                        </div>
                        <div>
                          <Label className="text-sm font-medium">Auto-generate Suggestions</Label>
                          <p className="text-xs text-muted-foreground">
                            Automatically generate resume suggestions
                          </p>
                        </div>
                      </div>
                      <Switch
                        checked={aiPreferences.autoGenerateSuggestions}
                        onCheckedChange={(checked) => updateAiPreferences('autoGenerateSuggestions', checked)}
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-primary/10">
                          <BarChart3 className="h-4 w-4 text-primary" />
                        </div>
                        <div>
                          <Label className="text-sm font-medium">AI Revenue Insights</Label>
                          <p className="text-xs text-muted-foreground">
                            Get AI-powered revenue predictions and trends
                          </p>
                        </div>
                      </div>
                      <Switch
                        checked={aiPreferences.aiInsights}
                        onCheckedChange={(checked) => updateAiPreferences('aiInsights', checked)}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="goals" className="space-y-6">
            <GoalsSettings />
          </TabsContent>

          <TabsContent value="revenue" className="space-y-6">
            {/* Revenue Goal Settings */}
            <Card className="bg-card text-card-foreground shadow-sm hover:shadow-md transition-all duration-200">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-foreground">Revenue Goal Settings</CardTitle>
                    <CardDescription className="text-muted-foreground">
                      Set and track your revenue targets
                    </CardDescription>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleResetGoals}
                    className="text-destructive hover:text-destructive/90"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Reset Goals
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Goal Amount</Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                        <Input
                          type="number"
                          value={revenueGoal.amount}
                          onChange={(e) => updateRevenueGoal('amount', parseInt(e.target.value))}
                          className="pl-8"
                          placeholder="Enter amount"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Duration (months)</Label>
                      <select
                        value={revenueGoal.duration}
                        onChange={(e) => updateRevenueGoal('duration', parseInt(e.target.value))}
                        className="w-full rounded-lg px-3 py-2 bg-muted border border-border text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20"
                      >
                        <option value="3">3 months</option>
                        <option value="6">6 months</option>
                        <option value="12">12 months</option>
                      </select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Start Date</Label>
                    <Input
                      type="date"
                      value={revenueGoal.startDate}
                      onChange={(e) => updateRevenueGoal('startDate', e.target.value)}
                      className="w-full"
                    />
                  </div>

                  {/* Revenue Metrics */}
                  <div className="p-4 rounded-lg bg-muted/50 border border-border/50">
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <div className="text-xs text-muted-foreground mb-1">Monthly Target</div>
                        <div className="text-sm font-medium text-foreground">
                          ${revenueInsights.monthlyTarget.toLocaleString()}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground mb-1">Current Monthly</div>
                        <div className="text-sm font-medium text-foreground">
                          ${revenueInsights.monthlyProgress.toLocaleString()}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground mb-1">Monthly Increase</div>
                        <div className={`text-sm font-medium flex items-center ${revenueInsights.monthlyIncrease >= 0 ? 'text-emerald-500' : 'text-destructive'
                          }`}>
                          {revenueInsights.monthlyIncrease >= 0 ? (
                            <ArrowUpRight className="h-3 w-3 mr-0.5" />
                          ) : (
                            <ArrowDownRight className="h-3 w-3 mr-0.5" />
                          )}
                          {Math.abs(revenueInsights.monthlyIncrease).toFixed(1)}%
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground mb-1">Required Increase</div>
                        <div className="text-sm font-medium text-foreground">
                          {revenueInsights.requiredMonthlyIncrease.toFixed(1)}%
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm font-medium">Current Progress</Label>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-muted-foreground">
                          {progressPercentage.toFixed(1)}%
                        </span>
                        {revenueInsights.isOnTrack ? (
                          <span className="flex items-center text-xs text-emerald-500">
                            <ArrowUpRight className="h-3 w-3 mr-0.5" />
                            On Track
                          </span>
                        ) : (
                          <span className="flex items-center text-xs text-destructive">
                            <ArrowDownRight className="h-3 w-3 mr-0.5" />
                            Behind
                          </span>
                        )}
                      </div>
                    </div>
                    <Progress
                      value={progressPercentage}
                      className={`h-2 ${revenueInsights.isOnTrack ? 'bg-emerald-500/20' : 'bg-destructive/20'
                        }`}
                    />
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>${revenueGoal.currentProgress.toLocaleString()}</span>
                      <span>${revenueGoal.amount.toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="advanced" className="space-y-6">
            <Card className="bg-card text-card-foreground shadow-sm hover:shadow-md transition-all duration-200">
              <CardHeader>
                <CardTitle className="text-foreground">Advanced Settings</CardTitle>
                <CardDescription className="text-muted-foreground">
                  Advanced configuration options
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 rounded-lg border border-border bg-muted/30">
                  <div className="flex items-center gap-2 mb-3">
                    <Activity className="h-4 w-4 text-primary" />
                    <Label className="text-sm font-medium">Tracking Configuration</Label>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-muted-foreground">Data Retention (days)</Label>
                      <Input
                        type="number"
                        defaultValue="365"
                        className="h-8 text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Update Frequency (minutes)</Label>
                      <Input
                        type="number"
                        defaultValue="5"
                        className="h-8 text-sm"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default memo(LinearSettings);